import { useEffect, useState, type ReactElement } from 'react';
import { NavLink } from 'react-router-dom';
import { ClipboardList, ClipboardPenLine, LogOut, PanelLeft, PanelLeftClose } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils/cn';
import { ROUTES } from '@/lib/constants/routes';
import { useUiStore } from '@/store/ui.store';
import { ENV_CONFIG } from '@/config/env';
import LogoCollapsed from '@/assets/logo-collapsed.svg';
import LogoExpanded from '@/assets/logo-expanded.svg';

interface NavigationBarProps {
  isOpen?: boolean;
  onClose?: () => void;
  onExpandChange?: (expanded: boolean) => void;
}

/**
 * Collapsible left rail (planning-v2 style): nav icons, expand/collapse, sign out.
 */
export function NavigationBar({
  isOpen = false,
  onClose,
  onExpandChange,
}: NavigationBarProps): ReactElement {
  const { signOut } = useAuth();
  const isMobile = useUiStore((s) => s.isMobile);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isMobile && !isOpen) {
      setIsExpanded(false);
    }
  }, [isMobile, isOpen]);

  const handleLogout = async (): Promise<void> => {
    try {
      await signOut();
    } finally {
      if (isMobile && onClose) {
        onClose();
      }
    }
  };

  const toggleExpanded = (): void => {
    const next = !isExpanded;
    setIsExpanded(next);
    if (onExpandChange && !isMobile) {
      onExpandChange(next);
    }
  };

  const handleNavClick = (): void => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  const showExpanded = isMobile ? isOpen : isExpanded;
  const navWidth = isMobile || showExpanded ? 'w-[260px]' : 'w-[60px]';

  const navLinkClass = (isActive: boolean): string =>
    cn(
      'flex items-center gap-3 rounded-full transition-all duration-300 ease-in-out touch-target',
      showExpanded ? 'h-12 w-full pl-[10px] pr-4' : 'h-10 w-10 justify-center',
      isActive
        ? 'bg-[#B1ECE2] text-[#1E293B]'
        : 'text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B]'
    );

  return (
    <>
      {isMobile && isOpen ? (
        <div
          className="fixed inset-0 z-90 bg-black/50 transition-opacity duration-300"
          aria-hidden
          onClick={onClose}
        />
      ) : null}

      <aside
        className={cn(
          'fixed left-0 top-0 z-100 flex h-screen flex-col bg-[#E7ECFC] pt-6 transition-all duration-500 ease-in-out',
          navWidth,
          showExpanded
            ? 'items-start pl-[15px] pr-[15px] pb-6'
            : 'items-center pl-[15px] pr-[15px] pb-[15px]',
          isMobile && !isOpen && '-translate-x-full'
        )}
      >
        <div className={cn('mb-8 flex h-[46px] items-center', showExpanded ? 'w-full' : 'w-10')}>
          {showExpanded ? (
            <img
              src={LogoExpanded}
              alt="CLIV Logo"
              className="h-[46px] w-auto transition-opacity duration-300"
            />
          ) : (
            <img
              src={LogoCollapsed}
              alt="CLIV Logo"
              className="h-[46px] w-auto transition-opacity duration-300"
            />
          )}
        </div>

        <nav className={cn('flex flex-1 flex-col gap-2', showExpanded && 'w-full')} aria-label="Main">
          <NavLink
            to={ROUTES.BOOKING_REQUESTS}
            end
            onClick={handleNavClick}
            className={({ isActive }) => navLinkClass(isActive)}
            title="Booking requests"
          >
            <div className="flex h-5 w-5 shrink-0 items-center justify-center">
              <ClipboardList className="h-5 w-5" strokeWidth={1.5} aria-hidden />
            </div>
            {showExpanded ? (
              <span className="text-sm font-medium transition-opacity duration-300">Booking requests</span>
            ) : null}
          </NavLink>
          <NavLink
            to={ROUTES.INSPECTIONS}
            end
            onClick={handleNavClick}
            className={({ isActive }) => navLinkClass(isActive)}
            title="Inspections"
          >
            <div className="flex h-5 w-5 shrink-0 items-center justify-center">
              <ClipboardPenLine className="h-5 w-5" strokeWidth={1.5} aria-hidden />
            </div>
            {showExpanded ? (
              <span className="text-sm font-medium transition-opacity duration-300">Inspections</span>
            ) : null}
          </NavLink>
        </nav>

        <div className={cn('flex flex-col gap-3', showExpanded && 'w-full')}>
          {!isMobile ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleExpanded}
              className={cn(
                'rounded-lg text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B] touch-target transition-all duration-300 ease-in-out',
                showExpanded ? 'h-12 w-full justify-start pl-[10px] pr-4 gap-3' : 'h-10 w-10'
              )}
              title={showExpanded ? 'Collapse menu' : 'Expand menu'}
            >
              <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                {showExpanded ? (
                  <PanelLeftClose className="h-5 w-5" strokeWidth={1.5} aria-hidden />
                ) : (
                  <PanelLeft className="h-5 w-5" strokeWidth={1.5} aria-hidden />
                )}
              </div>
              {showExpanded ? (
                <span className="text-sm font-medium transition-opacity duration-300">Collapse</span>
              ) : null}
            </Button>
          ) : null}

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => void handleLogout()}
            className={cn(
              'rounded-lg text-[#64748B] hover:bg-[#B1ECE2] hover:text-[#1E293B] touch-target transition-all duration-300 ease-in-out',
              showExpanded ? 'h-12 w-full justify-start gap-3 pl-[10px] pr-4' : 'h-10 w-10'
            )}
            title="Sign out"
          >
            <div className="flex h-5 w-5 shrink-0 items-center justify-center">
              <LogOut className="h-5 w-5" strokeWidth={1.5} aria-hidden />
            </div>
            {showExpanded ? (
              <span className="text-sm font-medium transition-opacity duration-300">Sign out</span>
            ) : null}
          </Button>

          <div className={cn('mt-1 text-[9px] text-[#64748B]', showExpanded ? 'text-left' : 'text-center')}>
            {showExpanded ? (
              <span>Version {ENV_CONFIG.APP_VERSION}</span>
            ) : (
              <span title={ENV_CONFIG.APP_VERSION}>{ENV_CONFIG.APP_VERSION}</span>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
